/**
 * One search box instead of a row of dropdowns.
 *
 * Whitespace splits the query into tokens and every token must match, so a rep
 * narrows in the order they think: `mine won 3` is my leads, that we won, that
 * scored top marks. Each feature decides what a single token means; this only
 * owns the splitting, the AND, and the one awkward case that both callers hit.
 *
 * That case: a multi-word label — a stage called "Tilbud sendt", a country
 * called "New Zealand" — can never match token by token, because neither word
 * is a legal value on its own. So the whole trimmed phrase is offered first,
 * and only if nothing claims it does the query split.
 */

export interface TokenSearchOptions {
  /**
   * True when the whole query is a single meaningful phrase that would be
   * destroyed by splitting. Checked before tokenising.
   */
  isPhrase?: (phrase: string) => boolean
}

export function matchesTokens(
  term: string,
  matchToken: (token: string) => boolean,
  { isPhrase }: TokenSearchOptions = {},
): boolean {
  const trimmed = term.trim().toLowerCase()
  if (!trimmed) return true

  if (isPhrase?.(trimmed)) return matchToken(trimmed)

  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => matchToken(token))
}

/** Lowercased, space-joined text of whatever fields are worth searching. */
export function haystack(parts: (string | number | null | undefined)[]): string {
  return parts
    .filter((p) => p !== null && p !== undefined && p !== '')
    .join(' ')
    .toLowerCase()
}
